import React, { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import throttle from 'lodash/throttle';

import { isEmptyString, isConsumerDomain, validEmail } from 'Shared/helpers';
import CsrfInput from 'Dashboard/components/CsrfInput/CsrfInput';
import TextField from 'Shared/components/TextField/TextField';
import { backOnboardingStateAction } from 'Shared/actions/onboardingState';

import { WpButton, WpButtonGroup } from 'Wpds/components/button';

type Props = {
  actionPath: string;
  currentEmail?: string;
  showBackButton?: boolean;
  inviterSlug?: string;
  workspaceSlug?: string;
  invitationToken?: string;
};

function CreateAccountForm({
  showBackButton,
  inviterSlug,
  workspaceSlug,
  actionPath,
  currentEmail,
  invitationToken,
}: Props) {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({ 'user[email]': '', 'user[password]': '' });
  const [emailErrorMessage, setEmailErrorMessage] = useState(null);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState(null);

  const checkWorkEmail = useCallback(email => {
    if (isConsumerDomain(email) || !validEmail(email?.trim())) setEmailErrorMessage('Please use a valid work email');
    else setEmailErrorMessage(null);
  }, []);

  const checkPassword = useCallback(password => {
    if (password.length < 6) setPasswordErrorMessage('Please use a password with at least 6 characters');
    else if (new Set(password).size == 1)
      setPasswordErrorMessage('Your password cannot consist of a repeated character');
    else setPasswordErrorMessage(null);
  }, []);

  const throttleCheckWorkEmail = useCallback(
    throttle(email => checkWorkEmail(email), 500, { leading: false, trailing: true }),
    [checkWorkEmail]
  );

  const handleTextFieldChange = event => {
    if (event.target.name === 'user[email]') throttleCheckWorkEmail(event.target.value);
    if (event.target.name == 'user[password]') checkPassword(event.target.value);
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleSubmitClick = () => {
    setIsSubmitting(true);
    formRef.current.submit();
  };

  const handleBackClick = () => dispatch(backOnboardingStateAction());

  const isSubmitDisabled =
    isSubmitting ||
    isEmptyString(formValues['user[email]'] || currentEmail) ||
    isEmptyString(formValues['user[password]']) ||
    emailErrorMessage ||
    passwordErrorMessage;

  return (
    <>
      <div className="mt-3 mb-2">Sign up with email {currentEmail || ''}</div>
      <form action={actionPath} ref={formRef} method="POST" className="w-100">
        <CsrfInput />
        {!currentEmail && (
          <TextField
            label="Work Email"
            name="user[email]"
            type="text"
            placeholder="Your Work Email"
            errorMessage={emailErrorMessage}
            value={formValues['user[email]']}
            onChange={handleTextFieldChange}
          />
        )}
        <TextField
          label="Create Secure Password"
          name="user[password]"
          type="password"
          errorMessage={passwordErrorMessage}
          value={formValues['user[password]']}
          onChange={handleTextFieldChange}
        />
        {inviterSlug && <input className="d-none" name="inviter_slug" value={inviterSlug} readOnly />}
        {workspaceSlug && <input className="d-none" name="workspace_slug" value={workspaceSlug} readOnly />}
        {invitationToken && (
          <>
            <input type="hidden" name="user[invitation_token]" value={invitationToken} />
            <input type="hidden" name="_method" value="put" />
          </>
        )}
        <div className="d-flex flex-column justify-content-start mt-4">
          <WpButtonGroup stacked>
            <WpButton
              btnType="submit"
              loading={isSubmitting}
              block
              size="lg"
              onClick={handleSubmitClick}
              disabled={isSubmitDisabled}
            >
              Create Account
            </WpButton>
            {showBackButton && (
              <WpButton disabled={isSubmitting} onClick={handleBackClick} variant="tertiary" size="lg" block>
                Back
              </WpButton>
            )}
          </WpButtonGroup>
        </div>
      </form>
    </>
  );
}

export default CreateAccountForm;
